import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Form, Space, Input, message } from 'antd';
import { useQuery, useApolloClient } from '@apollo/client';
import { GET_ACTIVE_TAGS, GET_ACTIVE_PRICE_SEGMENTATIONS, useBulkUpdateCustomers } from '../../mutations/customers';

const { Option } = Select;

const MaintenanceModal = ({ visible, onClose, selectedCustomers = [], onSuccess }) => {
  const [form] = Form.useForm();
  const [tagsAction, setTagsAction] = useState('keep');
  const [activeAction, setActiveAction] = useState('keep');
  const [priceTableAction, setPriceTableAction] = useState('keep');
  const [favoriteProductAction, setFavoriteProductAction] = useState('keep');
  const [tags, setTags] = useState([]);
  const [priceSegmentations, setPriceSegmentations] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // Apollo Client para invalidação de cache
  const client = useApolloClient();

  // Mutation hook
  const [bulkUpdateCustomers, { loading: mutationLoading }] = useBulkUpdateCustomers();

  // Query para carregar tags ativas
  const { loading: tagsLoading, data: tagsData } = useQuery(GET_ACTIVE_TAGS, {
    fetchPolicy: 'network-only'
  });
  
  // Query para carregar tabelas de preço ativas
  const { loading: priceLoading, data: priceData } = useQuery(GET_ACTIVE_PRICE_SEGMENTATIONS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (tagsData && tagsData.activeTags) {
      setTags(tagsData.activeTags);
    }
  }, [tagsData]);

  useEffect(() => {
    if (priceData && priceData.activePriceSegmentations) {
      setPriceSegmentations(priceData.activePriceSegmentations);
    }
  }, [priceData]);

  const actionOptions = [
    { value: 'keep', label: 'Manter valor existente' },
    { value: 'replace', label: 'Substituir valor existente por...' },
    { value: 'add', label: 'Adicionar aos valores existentes...' },
    { value: 'remove', label: 'Remover valor existente' }
  ];

  const activeActionOptions = [
    { value: 'keep', label: 'Manter valor existente' },
    { value: 'replace', label: 'Substituir valor existente por...' }
  ];

  const handleBulkUpdate = async () => {
    try {
      setErrorMessage('');
      
      // Obter valores dos formulários
      const formValues = form.getFieldsValue();
      
      // Montar variáveis da mutation
      const variables = {
        customerIds: selectedCustomers
      };

      // Mapear ações para inglês
      const actionMap = {
        'keep': 'KEEP_EXISTING',
        'replace': 'REPLACE_WITH',
        'add': 'ADD_TO_EXISTING',
        'remove': 'REMOVE_FROM_EXISTING'
      };

      // Tags Update
      if (tagsAction !== 'keep') {
        variables.tagsAction = actionMap[tagsAction];
        variables.tagIds = formValues.selectedTags || [];
      }

      // Active Update
      if (activeAction !== 'keep') {
        variables.activeAction = actionMap[activeAction];
        variables.active = formValues.selectedActive === 'active' ? true : false;
      }

      // Price Segmentation Update
      if (priceTableAction !== 'keep') {
        variables.priceSegmentationAction = actionMap[priceTableAction];
        variables.priceSegmentationIds = formValues.selectedPriceSegmentations || [];
      }

      // Favorite Product Update
      if (favoriteProductAction !== 'keep') {
        variables.favoriteProductAction = actionMap[favoriteProductAction];
        variables.favoriteProduct = formValues.favoriteProduct || null;
      }
      // Executar mutation
      const { data } = await bulkUpdateCustomers({ variables });
      
      if (data.bulkUpdateCustomers.success) {
        message.success(`${data.bulkUpdateCustomers.updatedCount} cliente(s) atualizado(s) com sucesso`);
        
        // Invalidar cache dos clientes individuais que foram atualizados
        selectedCustomers.forEach(customerId => {
          client.cache.evict({
            id: client.cache.identify({ __typename: 'Customer', id: customerId })
          });
        });
        
        // Limpar cache órfão
        client.cache.gc();
        
        handleCancel(); // Fechar modal e resetar form
        if (onSuccess) {
          onSuccess(); // Callback para recarregar listagem mantendo seleção e filtros
        }
      } else {
        // Mostrar erros sem fechar o modal
        const errors = data.bulkUpdateCustomers.errors || [];
        setErrorMessage(errors.join(', ') || 'Erro ao processar a solicitação');
      }
    } catch (error) {
      console.error('Erro na mutation:', error);
      setErrorMessage('Erro ao processar a solicitação');
    }
  };

  const handleApply = handleBulkUpdate;

  const handleCancel = () => {
    // Reset form and close modal
    form.resetFields();
    setTagsAction('keep');
    setActiveAction('keep');
    setPriceTableAction('keep');
    setFavoriteProductAction('keep');
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal
      title="Manutenção de Clientes"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancelar
        </Button>,
        <Button 
          key="apply" 
          type="primary" 
          onClick={handleApply}
          loading={mutationLoading}
          disabled={mutationLoading}
        >
          Aplicar
        </Button>
      ]}
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <strong>{selectedCustomers.length} cliente(s) selecionado(s)</strong>
      </div>

      {errorMessage && (
        <div style={{ marginBottom: 16, padding: 8, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, color: '#ff4d4f' }}>
          {errorMessage}
        </div>
      )}

      <Form form={form} layout="vertical">
        {/* Tags Field */}
        <Form.Item label="Tags">
          <Select
            value={tagsAction}
            onChange={setTagsAction}
            placeholder="Selecione uma ação"
          >
            {actionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {tagsAction !== 'keep' && (
          <Form.Item name="selectedTags">
            <Select
              placeholder={tagsLoading ? "Carregando tags..." : "Selecione as tags"}
              mode="multiple"
              style={{ marginTop: 8 }}
              loading={tagsLoading}
            >
              {tags.map(tag => (
                <Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Active Field */}
        <Form.Item label="Status">
          <Select
            value={activeAction}
            onChange={setActiveAction}
            placeholder="Selecione uma ação"
          >
            {activeActionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {activeAction !== 'keep' && (
          <Form.Item name="selectedActive">
            <Select
              placeholder="Selecione o status"
              style={{ marginTop: 8 }}
            >
              <Option value="active">Ativo</Option>
              <Option value="inactive">Inativo</Option>
            </Select>
          </Form.Item>
        )}

        {/* Price Table Field */}
        <Form.Item label="Tabela de Preço">
          <Select
            value={priceTableAction}
            onChange={setPriceTableAction}
            placeholder="Selecione uma ação"
          >
            {actionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {priceTableAction !== 'keep' && (
          <Form.Item name="selectedPriceSegmentations">
            <Select
              placeholder={priceLoading ? "Carregando tabelas de preço..." : "Selecione as tabelas de preço"}
              mode="multiple"
              style={{ marginTop: 8 }}
              loading={priceLoading}
            >
              {priceSegmentations.map(priceSegmentation => (
                <Option key={priceSegmentation.id} value={priceSegmentation.id}>
                  {priceSegmentation.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Favorite Product Field */}
        <Form.Item label="Produto Favorito">
          <Select
            value={favoriteProductAction}
            onChange={setFavoriteProductAction}
            placeholder="Selecione uma ação"
          >
            <Option value="keep">Manter valor existente</Option>
            <Option value="replace">Substituir valor existente por...</Option>
          </Select>
        </Form.Item>
        {favoriteProductAction === 'replace' && (
          <Form.Item name="favoriteProduct">
            <Input
              placeholder="Digite o produto favorito"
              style={{ marginTop: 8 }}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default MaintenanceModal;